import { useQuery } from "react-query";
import { parseISO, format, addMonths, subMonths } from "date-fns";

import { mlsGetMatches } from "../../shared/api/variables";
import { fetchClient } from "../../shared/api/fetching";
import { useReactAppState } from "../../shared/containers/utils";
import {
  composeFeaturedMatches,
  groupMatchesByStatus,
  sortMatchesByDate,
} from "../utils";
import { formatStatsAPIMatches } from "../../mls-horizontal-scoreboard/utils";
import { filterMlsMatchesByCompetitionsToSkip } from "../../mls-match-list/utils";

function useFeaturedMatchMLSMatches({
  seasonSportecId,
  matchSportecId,
  matchDate,
  dateFrom,
  dateTo,
  competitionSportecId,
  clubSportecId,
  previousMatchesCount,
  nextMatchesCount,
}) {
  const { apiList } = useReactAppState();

  const rangeOfDates = matchDate
    ? {
        from: format(subMonths(parseISO(matchDate), 2), "yyyy-MM-dd"),
        to: format(addMonths(parseISO(matchDate), 2), "yyyy-MM-dd"),
      }
    : dateFrom && dateTo
    ? {
        from: dateFrom,
        to: dateTo,
      }
    : {
        from: format(subMonths(new Date(), 2), "yyyy-MM-dd"),
        to: format(addMonths(new Date(), 2), "yyyy-MM-dd"),
      };

  const mlsMatchesEndpoint = mlsGetMatches({
    seasonSportecId,
    matchSportecId,
    from: rangeOfDates.from,
    to: rangeOfDates.to,
    competitionSportecId,
    clubSportecId,
    sort: "start_date:asc,home_team_name:asc",
    perPage: 500,
  });

  return useQuery({
    queryKey: [
      "mls-matches",
      clubSportecId,
      competitionSportecId,
      rangeOfDates.from,
      rangeOfDates.to,
      seasonSportecId,
    ],
    queryFn: () =>
      fetchClient({ endpoint: mlsMatchesEndpoint, apiURL: apiList?.statsAPI }),
    select: (data) => {
      const sortedMatches = formatStatsAPIMatches(
        sortMatchesByDate(filterMlsMatchesByCompetitionsToSkip(data?.schedule))
      );
      if (matchSportecId) {
        let previousMatches = [],
          featuredMatch = null,
          nextMatches = [];
        sortedMatches.forEach((match, index) => {
          if (match.match_id === matchSportecId) {
            previousMatches = previousMatchesCount
              ? sortedMatches.slice(index - previousMatchesCount, index)
              : [];
            featuredMatch = match;
            nextMatches = nextMatchesCount
              ? sortedMatches.slice(index + 1, index + nextMatchesCount + 1)
              : [];
          }
        });

        return {
          previousMatches,
          featuredMatch,
          nextMatches,
        };
      }

      const { completedMatches, scheduledMatches } = groupMatchesByStatus(
        sortedMatches
      );

      const result = composeFeaturedMatches({
        completedMatches,
        scheduledMatches,
        previousMatchesCount,
        nextMatchesCount: nextMatchesCount,
      });

      return {
        previousMatches: result?.previousMatches,
        featuredMatch: result?.featuredMatch,
        nextMatches: result?.nextMatches,
      };
    },
    enabled: !!(
      seasonSportecId &&
      (matchSportecId || matchDate || (rangeOfDates?.from && rangeOfDates?.to))
    ),
  });
}

export default useFeaturedMatchMLSMatches;
